import type { Patch } from "@freightsimple/shared/types";
import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { DialogResult } from "@freightsimple/ui-core/Dialog/DialogResult";
import { dangerousGoodsIsClass1Explosive } from "../Helpers/dangerousGoodsIsClass1Explosive";
import { dangerousGoodsRequiresTechnicalName } from "../Helpers/dangerousGoodsRequiresTechnicalName";
import {
  isDangerousNumberPackagesOutOfRange,
  maxDangerousNetExplosiveQuantityKg,
} from "../Validators/dangerousGoodsLimits";
import { useLineItemsShipmentLimits } from "@/Shared/LineItems/Validators/useLineItemsShipmentLimits";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import { useState } from "react";
import { DangerousGoodsTabs } from "./Internal/DangerousGoodsTabs";
import { EnterDetailsTabContent } from "./Internal/EnterDetailsTabContent";
import { LookupTabContent } from "./Internal/LookupTabContent";

interface SelectUNNumberDialogProps extends DialogBaseProps {
  lineItem: Patch<LineItem>;
  onDone: (_: Patch<LineItem>) => void;
  // Whether to collect the Canadian TDG net explosive quantity (Class 1 only).
  canadianTdgRules: boolean;
}

export function DangerousGoodsSelectUNNumberDialog(
  props: SelectUNNumberDialogProps,
) {
  const [initial, setInitial] = useState("");
  const [lineItem, setLineItem] = useState(props.lineItem);
  // The field bounds are the backend's shipment limits, so the dialog blocks on
  // the same numbers the API enforces.
  const shipmentLimits = useLineItemsShipmentLimits();

  function onChange(updates: Patch<LineItem>) {
    setLineItem(function (originalLineItem) {
      return {
        ...originalLineItem,
        ...updates,
      };
    });
  }

  function onLookup(
    _dangerousUnNumber: string,
    _dangerousClassification: string,
    _dangerousProperShippingName: string,
    _dangerousPackingGroup: string | undefined,
  ) {
    onChange({
      isDangerous: true,
      dangerousUnNumber: _dangerousUnNumber,
      dangerousClassification: _dangerousClassification,
      dangerousProperShippingName: _dangerousProperShippingName,
      dangerousPackingGroup: _dangerousPackingGroup,
      // A new lookup is a new commodity, so any previously entered net
      // explosive quantity no longer applies.
      dangerousNetExplosiveQuantityKg: undefined,
    });
  }

  const [selectedTab, setSelectedTab] = useState(
    props.lineItem.isDangerous
      ? DangerousGoodsTabs.EnterDetails
      : DangerousGoodsTabs.Lookup,
  );
  const [forceValidation, setForceValidation] = useState(false);

  const onLookupStep = selectedTab === DangerousGoodsTabs.Lookup;

  // Advancing to the details step — not finishing — so the dialog stays open.
  function onNext(): DialogResult {
    if (lineItem.dangerousUnNumber === undefined) {
      setForceValidation(true);
      return DialogResult.KeepOpen;
    }
    setForceValidation(false);
    setSelectedTab(DangerousGoodsTabs.EnterDetails);
    return DialogResult.KeepOpen;
  }

  function onChangeUnNumber() {
    setInitial(lineItem.dangerousUnNumber || "");
    setForceValidation(false);
    setSelectedTab(DangerousGoodsTabs.Lookup);
    setLineItem(props.lineItem);
  }

  // oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
  function onDone(): DialogResult {
    setForceValidation(true);

    const packagingTypeTooLong =
      lineItem.dangerousPackagingType !== undefined &&
      lineItem.dangerousPackagingType.length >
        shipmentLimits.dangerousGoods.maxPackagingTypeLength;
    const numberPackagesOutOfRange = isDangerousNumberPackagesOutOfRange(
      lineItem.dangerousNumberPackages,
      shipmentLimits.dangerousGoods.maxNumberPackages,
    );
    const technicalNameTooLong =
      lineItem.dangerousTechnicalName !== undefined &&
      lineItem.dangerousTechnicalName.length >
        shipmentLimits.dangerousGoods.maxTechnicalNameLength;

    // Special provision 16 makes the technical name mandatory for this UN number,
    // on the Canada-touching shipments Canadian TDG governs.
    const technicalNameMissing =
      props.canadianTdgRules &&
      dangerousGoodsRequiresTechnicalName(lineItem.dangerousUnNumber) &&
      (lineItem.dangerousTechnicalName ?? "").trim() === "";

    // Class 1 explosives on Canada-touching shipments must declare a positive
    // net explosive quantity (Canadian TDG), and it can't exceed the weight of
    // the item carrying it. Enforce it here so it blocks the same way every
    // other dangerous field does.
    const netExplosiveQuantityInvalid =
      props.canadianTdgRules &&
      dangerousGoodsIsClass1Explosive(lineItem.dangerousClassification) &&
      (lineItem.dangerousNetExplosiveQuantityKg === undefined ||
        lineItem.dangerousNetExplosiveQuantityKg <= 0 ||
        lineItem.dangerousNetExplosiveQuantityKg >
          maxDangerousNetExplosiveQuantityKg(
            lineItem,
            shipmentLimits.dangerousGoods.maxNetExplosiveQuantityKg,
          ));

    if (
      lineItem.dangerousUnNumber !== undefined &&
      lineItem.dangerousClassification !== undefined &&
      lineItem.dangerousPackingGroup !== undefined &&
      lineItem.dangerousProperShippingName !== undefined &&
      lineItem.dangerousNumberPackages !== undefined &&
      lineItem.dangerousPackagingType !== undefined &&
      !packagingTypeTooLong &&
      !numberPackagesOutOfRange &&
      !technicalNameTooLong &&
      !technicalNameMissing &&
      !netExplosiveQuantityInvalid
    ) {
      props.onDone(lineItem);
      return DialogResult.Close;
    }

    // Validation failed — hold the dialog open so the errors are visible.
    return DialogResult.KeepOpen;
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Dangerous Goods Details"
      description="To ship dangerous goods, we need to know some more details. Start by either entering the UN number for this commodity, or search by description"
      okText={onLookupStep ? "Next" : "Done"}
      cancelText="Cancel"
      onOk={onLookupStep ? onNext : onDone}
    >
      <ControlPresentationProvider presentation="underlined">
        {onLookupStep ? (
          <LookupTabContent
            initial={initial}
            forceValidation={forceValidation}
            onLookup={onLookup}
            onResetForceValidation={function () {
              setForceValidation(false);
            }}
          />
        ) : (
          <EnterDetailsTabContent
            forceValidation={forceValidation}
            lineItem={lineItem}
            shipmentLimits={shipmentLimits}
            onChange={onChange}
            onChangeUnNumber={onChangeUnNumber}
            canadianTdgRules={props.canadianTdgRules}
          />
        )}
      </ControlPresentationProvider>
    </Dialog>
  );
}
